import React, { useState, useEffect, useRef } from "react";
import { InputBase, Theme, alpha, Paper, List, ListItem, ListItemText, Typography, ClickAwayListener } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from "react-i18next";
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import SearchServiceInstance from "../../services/SearchService";
import { ISearchResult, ISearchEntity } from "../../models/ISearchResult";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        search: {
            position: 'relative',
            borderRadius: theme.shape.borderRadius,
            backgroundColor: alpha(theme.palette.common.white, 0.15),
            '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.25),
            },
            marginRight: theme.spacing(2),
            marginLeft: 0,
            width: '100%',
            [theme.breakpoints.up('sm')]: {
                marginLeft: theme.spacing(3),
                width: 'auto',
            },
        },
        searchIcon: {
            padding: theme.spacing(0, 2),
            height: '100%',
            position: 'absolute',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        inputRoot: {
            color: 'inherit',
        },
        inputInput: {
            padding: theme.spacing(1, 1, 1, 0),
            // vertical padding + font size from searchIcon
            paddingLeft: `calc(1em + ${theme.spacing(4)})`,
            transition: theme.transitions.create('width'),
            width: '100%',
            [theme.breakpoints.up('md')]: {
                width: '20ch',
            },
        },
        searchResults: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: theme.spacing(1),
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1300,
            backgroundColor: theme.palette.background.paper,
        },
        categoryHeader: {
            backgroundColor: theme.palette.grey[200],
            fontWeight: 'bold',
            paddingLeft: theme.spacing(2),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
        resultItem: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
        noResults: {
            padding: theme.spacing(2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        },
    })
);

export default function SearchControl() {
    const { t } = useTranslation('common');
    const classes = useStyles();
    const navigate = useNavigate();
    
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResults, setSearchResults] = useState<ISearchResult | null>(null);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Debounced search effect
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
            setSearchResults(null);
            setShowResults(false);
            return;
        }

        setIsLoading(true);
        debounceTimerRef.current = setTimeout(async () => {
            try {
                const results = await SearchServiceInstance.search(searchTerm);
                setSearchResults(results);
                setShowResults(true);
                setIsLoading(false);
            } catch (error) {
                console.error("Search error:", error);
                setSearchResults(null);
                setShowResults(false);
                setIsLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchTerm]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleClickAway = () => {
        setShowResults(false);
    };

    const handleResultClick = (category: string, entity: ISearchEntity) => {
        let route = "";
        switch (category) {
            case "riders":
                if (entity.entityId.length >= 2) {
                    route = `/rider/${entity.entityId[0]}/${entity.entityId[1]}`;
                }
                break;
            case "events":
                if (entity.entityId.length >= 1) {
                    route = `/event/${entity.entityId[0]}`;
                }
                break;
            case "circuits":
                if (entity.entityId.length >= 1) {
                    route = `/circuits/${entity.entityId[0]}`;
                }
                break;
            case "organizers":
                if (entity.entityId.length >= 1) {
                    route = `/organizers/${entity.entityId[0]}`;
                }
                break;
            default:
                break;
        }
        
        if (route) {
            navigate(route);
            setShowResults(false);
            setSearchTerm("");
        }
    };

    const hasResults = searchResults && (
        searchResults.riders.length > 0 ||
        searchResults.events.length > 0 ||
        searchResults.circuits.length > 0 ||
        searchResults.organizers.length > 0
    );

    const renderCategory = (title: string, items: ISearchEntity[], category: string) => {
        if (items.length === 0) return null;
        
        return (
            <>
                <div className={classes.categoryHeader}>
                    <Typography variant="subtitle2">{title}</Typography>
                </div>
                {items.map((item, index) => (
                    <ListItem
                        key={`${category}-${index}`}
                        className={classes.resultItem}
                        onClick={() => handleResultClick(category, item)}
                    >
                        <ListItemText primary={item.displayName} />
                    </ListItem>
                ))}
            </>
        );
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div className={classes.search}>
                <div className={classes.searchIcon}>
                    <SearchIcon />
                </div>
                <InputBase
                    placeholder={t<string>("bar.search")}
                    classes={{
                        root: classes.inputRoot,
                        input: classes.inputInput,
                    }}
                    inputProps={{ 'aria-label': 'search' }}
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {showResults && (
                    <Paper className={classes.searchResults} elevation={3}>
                        {isLoading ? (
                            <div className={classes.noResults}>
                                <Typography variant="body2">{t<string>("bar.searching")}</Typography>
                            </div>
                        ) : hasResults ? (
                            <List>
                                {searchResults && renderCategory(t<string>("mainList.events"), searchResults.events, "events")}
                                {searchResults && renderCategory(t<string>("mainList.riders"), searchResults.riders, "riders")}
                                {searchResults && renderCategory(t<string>("mainList.circuits"), searchResults.circuits, "circuits")}
                                {searchResults && renderCategory(t<string>("mainList.organizers"), searchResults.organizers, "organizers")}
                            </List>
                        ) : (
                            <div className={classes.noResults}>
                                <Typography variant="body2">{t<string>("bar.noResults")}</Typography>
                            </div>
                        )}
                    </Paper>
                )}
            </div>
        </ClickAwayListener>
    );
}